// src/frontend/src/pages/PrivacyPage.js
import { Box, Container, Typography, Paper } from '@mui/material';

const PrivacyPage = () => {
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                    Privacy Policy
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Last updated: August 15, 2026
                </Typography>

                <Box sx={{ '& h2': { mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8 } }}>
                    <Typography variant="h5" component="h2" fontWeight="600">
                        1. Introduction
                    </Typography>
                    <Typography variant="body1">
                        StockMaid ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, services at stockmaid.com, and the StockMaid Chrome extension, "StockMaid: GPU Price Match & Restock Alerts" (collectively, the "Service").
                    </Typography>
                    <Typography variant="body1">
                        By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies, please do not use our Service.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        2. Information We Collect
                    </Typography>
                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.1 Information from Google Sign-In
                    </Typography>
                    <Typography variant="body1">
                        When you sign in using Google OAuth, we collect:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Email address</Typography></li>
                        <li><Typography variant="body1">Name</Typography></li>
                        <li><Typography variant="body1">Profile picture URL</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.2 Information You Provide
                    </Typography>
                    <Typography variant="body1">
                        We collect information you voluntarily provide, including:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Product tracking preferences (which GPUs you want to track)</Typography></li>
                        <li><Typography variant="body1">Notification preferences (email notification settings)</Typography></li>
                        <li><Typography variant="body1">State preferences for in-store pickup notifications</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.3 Payment Information
                    </Typography>
                    <Typography variant="body1">
                        When you subscribe to our premium service, payment processing is handled entirely by Stripe. We do not store your credit card numbers, bank account details, or other sensitive payment information on our servers. We only receive and store a Stripe customer ID and subscription status.
                    </Typography>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.4 Automatically Collected Information
                    </Typography>
                    <Typography variant="body1">
                        When you access our Service, we may automatically collect:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">IP address (for rate limiting and security purposes)</Typography></li>
                        <li><Typography variant="body1">Browser type and version</Typography></li>
                        <li><Typography variant="body1">Pages visited and time spent</Typography></li>
                        <li><Typography variant="body1">Device information</Typography></li>
                    </Box>

                    <Typography variant="h6" component="h3" fontWeight="500" sx={{ mt: 2 }}>
                        2.5 Chrome Extension Data
                    </Typography>
                    <Typography variant="body1">
                        If you use the StockMaid Chrome extension, we may process additional data needed to provide price comparisons and the attended assistant workflow.
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Your StockMaid authentication token from a signed-in StockMaid tab in the same browser profile, so the extension can authenticate requests to your StockMaid account.</Typography></li>
                        <li><Typography variant="body1">Tracked product, availability, premium-status, and state-preference data returned by StockMaid APIs.</Typography></li>
                        <li><Typography variant="body1">Optional Best Buy details you choose to save in the extension, including email address, last name, and the last four digits of your phone number.</Typography></li>
                        <li><Typography variant="body1">Limited extension event data such as SKU, event type, result, reason text, timestamp, and extension version for diagnostics and support.</Typography></li>
                        <li><Typography variant="body1">On Best Buy, Micro Center, and Newegg product pages you open, the extension reads the page URL and visible product identifier (such as a SKU or item number) locally to recognize the product and show a price comparison card. Page contents are not collected; only the product identifier is sent to StockMaid to look up prices we already track.</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        3. How We Use Your Information
                    </Typography>
                    <Typography variant="body1">
                        We use the information we collect to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Provide, operate, and maintain our Service</Typography></li>
                        <li><Typography variant="body1">Send you stock availability notifications via email</Typography></li>
                        <li><Typography variant="body1">Process your subscription payments through Stripe</Typography></li>
                        <li><Typography variant="body1">Respond to your comments, questions, and support requests</Typography></li>
                        <li><Typography variant="body1">Monitor and analyze usage patterns to improve our Service</Typography></li>
                        <li><Typography variant="body1">Detect, prevent, and address technical issues and abuse</Typography></li>
                        <li><Typography variant="body1">Show price comparison cards on Best Buy, Micro Center, and Newegg product pages for products StockMaid tracks</Typography></li>
                        <li><Typography variant="body1">Operate the attended Chrome extension workflow, including watchlist polling, Best Buy cart handoff, pickup ZIP rotation, and optional Best Buy form autofill that you explicitly configure</Typography></li>
                        <li><Typography variant="body1">Comply with legal obligations</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        4. Information Sharing and Disclosure
                    </Typography>
                    <Typography variant="body1">
                        We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1"><strong>Service Providers:</strong> We share information with Stripe for payment processing and may use other service providers for email delivery and hosting.</Typography></li>
                        <li><Typography variant="body1"><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to valid legal requests.</Typography></li>
                        <li><Typography variant="body1"><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred.</Typography></li>
                        <li><Typography variant="body1"><strong>With Your Consent:</strong> We may share information with your explicit consent.</Typography></li>
                    </Box>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        5. Data Retention
                    </Typography>
                    <Typography variant="body1">
                        We retain your personal information for as long as your account is active or as needed to provide you services. If you wish to delete your account, please contact us at support@stockmaid.com. We will delete your personal information within 30 days of your request, except where we are required to retain it for legal purposes.
                    </Typography>
                    <Typography variant="body1">
                        The Chrome extension also stores configuration and temporary runtime state locally in Chrome extension storage on your device. You can remove that local extension data by uninstalling the extension or clearing its stored data in Chrome.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        6. Data Security
                    </Typography>
                    <Typography variant="body1">
                        We implement appropriate technical and organizational security measures to protect your personal information, including:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">HTTPS encryption for all data transmission</Typography></li>
                        <li><Typography variant="body1">Secure authentication using JWT tokens</Typography></li>
                        <li><Typography variant="body1">Rate limiting to prevent abuse</Typography></li>
                        <li><Typography variant="body1">Regular security reviews</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        7. Your Rights
                    </Typography>
                    <Typography variant="body1">
                        Depending on your location, you may have the following rights:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1"><strong>Access:</strong> Request a copy of your personal data</Typography></li>
                        <li><Typography variant="body1"><strong>Correction:</strong> Request correction of inaccurate data</Typography></li>
                        <li><Typography variant="body1"><strong>Deletion:</strong> Request deletion of your personal data</Typography></li>
                        <li><Typography variant="body1"><strong>Portability:</strong> Request transfer of your data</Typography></li>
                        <li><Typography variant="body1"><strong>Opt-out:</strong> Unsubscribe from marketing communications</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        To exercise these rights, please contact us at support@stockmaid.com.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        8. Cookies and Tracking
                    </Typography>
                    <Typography variant="body1">
                        We use cookies and similar technologies for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Authentication and session management</Typography></li>
                        <li><Typography variant="body1">Remembering your preferences</Typography></li>
                        <li><Typography variant="body1">Analytics (via Google Analytics)</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        You can control cookies through your browser settings, but disabling them may affect the functionality of our Service.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        9. Third-Party Services
                    </Typography>
                    <Typography variant="body1">
                        Our Service integrates with the following third-party services:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1"><strong>Google:</strong> For authentication (OAuth 2.0)</Typography></li>
                        <li><Typography variant="body1"><strong>Stripe:</strong> For payment processing</Typography></li>
                        <li><Typography variant="body1"><strong>Google Analytics:</strong> For website analytics</Typography></li>
                        <li><Typography variant="body1"><strong>Best Buy:</strong> The Chrome extension interacts with Best Buy pages you open in order to detect availability, attempt Add to Cart, and hand off cart completion to you</Typography></li>
                        <li><Typography variant="body1"><strong>Micro Center and Newegg:</strong> The Chrome extension reads product pages you open on these sites only to identify the product being viewed and display a price comparison card; it does not interact with or submit anything on these sites</Typography></li>
                    </Box>
                    <Typography variant="body1">
                        These services have their own privacy policies governing how they handle your data.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        10. Chrome Extension Limits
                    </Typography>
                    <Typography variant="body1">
                        The StockMaid extension is designed for attended use. It does not automate payment submission or final order placement, retrieve SMS codes, solve CAPTCHAs, or bypass retailer security controls.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        11. Children's Privacy
                    </Typography>
                    <Typography variant="body1">
                        Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        12. International Data Transfers
                    </Typography>
                    <Typography variant="body1">
                        Your information may be transferred to and processed in countries other than your own. By using our Service, you consent to such transfers. We take appropriate safeguards to ensure your data is protected in accordance with this Privacy Policy.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        13. Changes to This Privacy Policy
                    </Typography>
                    <Typography variant="body1">
                        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Continued use of the Service after changes constitutes acceptance of the updated policy.
                    </Typography>

                    <Typography variant="h5" component="h2" fontWeight="600">
                        14. Contact Us
                    </Typography>
                    <Typography variant="body1">
                        If you have any questions about this Privacy Policy, please contact us:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <li><Typography variant="body1">Email: support@stockmaid.com</Typography></li>
                        <li><Typography variant="body1">Discord: discord.gg/W78pdPCdbC</Typography></li>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default PrivacyPage;
